import * as amplitude from '@amplitude/analytics-browser';
import {
  PAYPAL_AMPLITUDE_DEVICE_ID_QUERY_PARAM,
  resolveShouldSkipAmplitudeForPaypalRedirect,
} from '../utils/payments/paypalAmplitudeDeviceGate';
import { resolveFrontendUserId } from '../utils/scripts/frontendUserId';

const hostname = window.location.hostname;
const isProd = hostname === 'webapp.everydoggy.com';

const PROD_KEY = '5c11432422c7701ad489cd6f279bfa65';
const DEV_KEY  = 'e61ae44b1ed70b865138a73e27cefd50';

const API_KEY = isProd ? PROD_KEY : DEV_KEY;
const AMP_TIMEOUT = 5000;

let ampLoaded = false;
type AmplitudeIdentify = InstanceType<typeof amplitude.Identify>;
let amplitudeGateState: 'allowed' | 'blocked' | 'pending' = 'allowed';
let pendingAmplitudeActions: Array<
  | { type: 'track'; eventName: string; eventProperties?: Record<string, unknown> }
  | { type: 'identify'; identify: AmplitudeIdentify }
> = [];

declare global {
  interface Window {
    __earlyErrors?: Array<Record<string, unknown>>;
    __amp_inited?: boolean;
    amplitude?: typeof amplitude;
  }
}

window.__earlyErrors = window.__earlyErrors || [];

function pushAmplitudeError(reason: string) {
  const error = {
    type: 'AmplitudeLoadError',
    reason,
    hostname,
    apiKey: API_KEY,
    userAgent: navigator.userAgent,
    timestamp: Date.now(),
  };

  window.__earlyErrors?.push(error);

  window.dispatchEvent(
    new CustomEvent('early-error', { detail: error })
  );
}

function hasPaypalAmplitudeDeviceIdOnPage(): boolean {
  try {
    return Boolean(
      new URL(window.location.href).searchParams.get(
        PAYPAL_AMPLITUDE_DEVICE_ID_QUERY_PARAM
      )
    );
  } catch {
    return false;
  }
}

function trackAmplitudeNow(
  eventName: string,
  eventProperties?: Record<string, unknown>
) {
  amplitude.track(eventName, eventProperties);
}

function identifyAmplitudeNow(identify: AmplitudeIdentify) {
  amplitude.identify(identify);
}

function flushPendingAmplitudeActions() {
  const actions = pendingAmplitudeActions;
  pendingAmplitudeActions = [];

  actions.forEach(action => {
    if (action.type === 'track') {
      trackAmplitudeNow(action.eventName, action.eventProperties);
      return;
    }

    identifyAmplitudeNow(action.identify);
  });
}

export function trackAmplitude(
  eventName: string,
  eventProperties?: Record<string, unknown>
) {
  if (amplitudeGateState === 'blocked') {
    return;
  }

  if (amplitudeGateState === 'pending') {
    pendingAmplitudeActions.push({
      type: 'track',
      eventName,
      eventProperties,
    });
    return;
  }

  trackAmplitudeNow(eventName, eventProperties);
}

export function identifyAmplitude(identify: AmplitudeIdentify) {
  if (amplitudeGateState === 'blocked') {
    return;
  }

  if (amplitudeGateState === 'pending') {
    pendingAmplitudeActions.push({
      type: 'identify',
      identify,
    });
    return;
  }

  identifyAmplitudeNow(identify);
}

export function initAmplitude() {
  if (window.__amp_inited) return;
  window.__amp_inited = true;

  // Resolved before init so the id is part of the initial config: every event of
  // this load, including the ones the SDK fires itself, goes out with it.
  // `undefined` leaves Amplitude's own stored user_id untouched.
  const frontendUserId = resolveFrontendUserId();

  try {
    amplitudeGateState = hasPaypalAmplitudeDeviceIdOnPage()
      ? 'pending'
      : 'allowed';

    amplitude.init(API_KEY, {
      userId: frontendUserId,
      fetchRemoteConfig: true,

      autocapture: {
        pageUrlEnrichment: true,
        elementInteractions: false,

        attribution: {
          excludeReferrers: ['everydoggy.com'],
        },
      },
    });

    ampLoaded = true;

    window.amplitude = amplitude;

    void (async () => {
      if (amplitudeGateState === 'pending') {
        try {
          const shouldSkip = await resolveShouldSkipAmplitudeForPaypalRedirect();
          amplitudeGateState = shouldSkip ? 'blocked' : 'allowed';
        } catch {
          amplitudeGateState = 'blocked';
        }

        if (amplitudeGateState === 'allowed') {
          flushPendingAmplitudeActions();
        } else {
          pendingAmplitudeActions = [];
        }
      }
    })();

    trackAmplitude('page_view');

    // identify
    const identify = new amplitude.Identify().set(
      'user_agent',
      navigator.userAgent
    );
    identifyAmplitude(identify);
  } catch (e) {
    pushAmplitudeError('init_exception');
  }

  setTimeout(() => {
    if (!ampLoaded) {
      pushAmplitudeError('init_timeout');
    }
  }, AMP_TIMEOUT);
}

export { amplitude };
